import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { DateRangeProvider } from "@/context/DateRangeContext";
import { VisibilityProvider } from "@/context/VisibilityContext";
import { AvatarProvider } from "@/context/AvatarContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import { AlertsProvider } from "@/context/AlertsContext";
import { LocaleProvider } from "@/context/LocaleContext";
import { AlertStack } from "@/components/alerts/AlertStack";
import { TeamChatWidget } from "@/components/team-chat/TeamChatWidget";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RequireModule } from "@/components/RequireModule";
import { VersionChecker } from "@/components/VersionChecker";
import { AppLayout } from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import { ClientPortalLayout } from "@/components/layout/ClientPortalLayout";
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AuthGoogleCallback = lazy(() => import("@/pages/AuthGoogleCallback"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ClientWorkspace = lazy(() => import("@/pages/ClientWorkspace"));
const Impostazioni = lazy(() => import("@/pages/Impostazioni"));
const SettingsUsers = lazy(() => import("@/pages/SettingsUsers"));
const SettingsIntegrations = lazy(() => import("@/pages/SettingsIntegrations"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Automazioni = lazy(() => import("@/pages/Automazioni"));
const AutomationBuilder = lazy(() => import("@/pages/AutomationBuilder"));
const CRM = lazy(() => import("@/pages/CRM"));
const LeadRouter = lazy(() => import("@/pages/LeadRouter"));
const ClientiRegistry = lazy(() => import("@/pages/ClientiRegistry"));
const MonthlyOperations = lazy(() => import("@/pages/MonthlyOperations"));
const EditorialPlanner = lazy(() => import("@/pages/EditorialPlanner"));
const Campaigns = lazy(() => import("@/pages/Campaigns"));
const CampaignWizard = lazy(() => import("@/pages/CampaignWizard"));
const SupportTickets = lazy(() => import("@/pages/SupportTickets"));
const PublicSupportForm = lazy(() => import("@/pages/PublicSupportForm"));
const EmbedDemoLanding = lazy(() => import("@/pages/EmbedDemoPage").then((m) => ({ default: m.EmbedDemoLanding })));
const EmbedDemoThankYou = lazy(() => import("@/pages/EmbedDemoPage").then((m) => ({ default: m.EmbedDemoThankYou })));
const EditorialPreview = lazy(() => import("@/pages/EditorialPreview"));
const ClientDetail = lazy(() => import("@/pages/ClientDetail"));
const Appunti = lazy(() => import("@/pages/Appunti"));
const AgentAccess = lazy(() => import("@/pages/AgentAccess"));
const TommyApprovals = lazy(() => import("@/pages/TommyApprovals"));
const Passwords = lazy(() => import("@/pages/Passwords"));
const ChatModule = lazy(() => import("@/pages/ChatModule"));
const Amministrazione = lazy(() => import("@/pages/Amministrazione"));
const Contabilita = lazy(() => import("@/pages/Contabilita"));
const HR = lazy(() => import("@/pages/HR"));
const Landing = lazy(() => import("@/pages/Landing"));
const LandingEditor = lazy(() => import("@/modules/Landing/LandingEditor"));
const LandingPublic = lazy(() => import("@/pages/LandingPublic"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const ProposalPreview = lazy(() => import("@/pages/ProposalPreview"));
const PublicBooking = lazy(() => import("@/pages/PublicBooking"));
const PublicBookingManage = lazy(() => import("@/pages/PublicBooking").then((m) => ({ default: m.PublicBookingManage })));
const Consensi = lazy(() => import("@/pages/Consensi"));
const PublicConsent = lazy(() => import("@/pages/PublicConsent"));
const CollabApproval = lazy(() => import("@/pages/CollabApproval"));
const ClientPortalDashboard = lazy(() => import("@/pages/ClientPortalDashboard"));
const ClientPortalAdmin = lazy(() => import("@/pages/ClientPortalAdmin"));
const ClientPortalSupport = lazy(() => import("@/pages/ClientPortalSupport"));
const ClientPortalPlanning = lazy(() => import("@/pages/ClientPortalPlanning"));
const ClientAccountSettings = lazy(() => import("@/pages/ClientAccountSettings"));

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <DateRangeProvider>
          <VisibilityProvider>
            <AvatarProvider>
            <PermissionsProvider>
            <BrowserRouter>
            <AlertsProvider>
            <LocaleProvider>
            <Suspense fallback={<div className="flex h-screen items-center justify-center" data-testid="route-loading"><div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" /></div>}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/google" element={<AuthGoogleCallback />} />
              <Route path="/preview/:token" element={<EditorialPreview />} />
              <Route path="/proposal/preview/:token" element={<ProposalPreview />} />
              <Route path="/prenota/gestisci/:token" element={<PublicBookingManage />} />
              <Route path="/prenota/:slug" element={<PublicBooking />} />
              <Route path="/consenso/:token" element={<PublicConsent />} />
              <Route path="/collab/:token" element={<CollabApproval />} />
              <Route path="/l/:slug" element={<LandingPublic />} />
              <Route path="/landing/:clientSlug/:slug" element={<LandingPublic />} />
              <Route path="/landing/:clientSlug/:slug/:pageSlug" element={<LandingPublic />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/support-form/:token" element={<PublicSupportForm />} />
              <Route path="/embed-demo/:key/grazie" element={<EmbedDemoThankYou />} />
              <Route path="/embed-demo/:key" element={<EmbedDemoLanding />} />
              <Route
                element={
                  <ProtectedRoute internalOnly>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/lead-router" element={<LeadRouter />} />
                <Route path="/clienti" element={<ClientiRegistry />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/automazioni" element={<Automazioni />} />
                <Route path="/automazioni/:id" element={<AutomationBuilder />} />
                <Route path="/crm" element={<CRM />} />
                <Route path="/clienti/:clientId/dettaglio" element={<ClientDetail />} />
                <Route path="/clienti/:clientId" element={<ClientWorkspace />} />
                <Route path="/tasks" element={<Navigate to="/tasks/operations" replace />} />
                <Route path="/tasks/operations" element={<MonthlyOperations />} />
                <Route path="/tasks/editorial" element={<EditorialPlanner />} />
                <Route path="/campagne" element={<Campaigns />} />
                <Route path="/campagne/:draftId" element={<CampaignWizard />} />
                <Route path="/tasks/support-tickets" element={<SupportTickets />} />
                <Route path="/appunti" element={<Appunti />} />
                <Route path="/landing" element={<Landing />} />
                <Route path="/landing/:id/editor" element={<LandingEditor />} />
                <Route path="/password" element={<Passwords />} />
                <Route path="/accessi-agenti" element={<AgentAccess />} />
                <Route path="/tommy" element={<TommyApprovals />} />
                <Route path="/consensi" element={<Consensi />} />
                <Route path="/chat" element={<ChatModule />} />
                <Route path="/amministrazione" element={<RequireModule moduleKey="amministrazione"><Amministrazione /></RequireModule>} />
                <Route path="/contabilita" element={<RequireModule moduleKey="contabilita"><Contabilita /></RequireModule>} />
                <Route path="/hr" element={<RequireModule moduleKey="hr"><HR /></RequireModule>} />
                <Route path="/impostazioni" element={<Impostazioni />} />
                <Route path="/settings/users" element={<SettingsUsers />} />
                <Route path="/settings/integrations" element={<SettingsIntegrations />} />
              </Route>
              <Route
                path="/portal"
                element={
                  <ProtectedRoute>
                    <ClientPortalLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<ClientPortalDashboard />} />
                <Route path="amministrazione" element={<ClientPortalAdmin />} />
                <Route path="assistenza" element={<ClientPortalSupport />} />
                <Route path="planning" element={<ClientPortalPlanning />} />
                <Route path="impostazioni" element={<ClientAccountSettings />} />
                <Route path=":clientId" element={<ClientPortalDashboard />} />
                <Route path=":clientId/amministrazione" element={<ClientPortalAdmin />} />
                <Route path=":clientId/assistenza" element={<ClientPortalSupport />} />
                <Route path=":clientId/planning" element={<ClientPortalPlanning />} />
                <Route path=":clientId/impostazioni" element={<ClientAccountSettings />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            <AlertStack />
            <TeamChatWidget />
            <Toaster position="bottom-right" />
            <VersionChecker />
            </LocaleProvider>
            </AlertsProvider>
          </BrowserRouter>
            </PermissionsProvider>
            </AvatarProvider>
          </VisibilityProvider>
        </DateRangeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
